import type { RadioStation } from '@/mocks/radios';
import { getRadioProfile, getRadioTeam, type RadioProfile } from '@/mocks/radioProfile';
import { useState } from 'react';

function InfoItem({ icon, label, value }: { icon: string; label: string; value: string }) {
  return (
    <div className="flex items-center gap-3 p-3 bg-background-100 rounded-xl">
      <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center shrink-0">
        <i className={`${icon} text-primary-600 text-base`}></i>
      </div>
      <div className="min-w-0">
        <p className="text-[11px] text-foreground-500 font-medium uppercase tracking-wider">{label}</p>
        <p className="text-sm font-semibold text-foreground-950 truncate">{value}</p>
      </div>
    </div>
  );
}

function ScheduleTable({ schedule }: { schedule: RadioProfile['schedule'] }) {
  return (
    <div className="overflow-hidden rounded-xl border border-background-200/70 bg-background-50">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-background-100/80 border-b border-background-200/70">
              <th className="text-left px-4 py-3 text-[11px] font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">Día</th>
              <th className="text-left px-4 py-3 text-[11px] font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">Horario</th>
              <th className="text-left px-4 py-3 text-[11px] font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">Programa</th>
              <th className="text-left px-4 py-3 text-[11px] font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">Locutor</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-background-200/50">
            {schedule.map((entry, index) => (
              <tr key={index} className="hover:bg-background-100/50 transition-colors">
                <td className="px-4 py-3 text-foreground-800 font-medium text-[13px] whitespace-nowrap">{entry.day}</td>
                <td className="px-4 py-3 text-foreground-600 text-[13px] whitespace-nowrap">{entry.timeRange}</td>
                <td className="px-4 py-3 text-foreground-800 font-semibold text-[13px] whitespace-nowrap">{entry.program}</td>
                <td className="px-4 py-3 text-foreground-600 text-[13px] whitespace-nowrap">{entry.host}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

interface SeccionAcercaProps {
  station: RadioStation;
}

export default function SeccionAcerca({ station }: SeccionAcercaProps) {
  const profile = getRadioProfile(station.id);
  const [activeTab, setActiveTab] = useState<'historia' | 'mision' | 'vision'>('historia');

  if (!profile) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <div className="w-20 h-20 mx-auto mb-5 rounded-full bg-foreground-100 flex items-center justify-center">
            <i className="ri-information-line text-3xl text-foreground-400"></i>
          </div>
          <h3 className="text-lg font-semibold text-foreground-950 mb-2">Información no disponible</h3>
          <p className="text-foreground-500 text-sm">Esta estación no tiene perfil completo en este momento.</p>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'historia' as const, label: 'Historia', icon: 'ri-book-open-line' },
    { id: 'mision' as const, label: 'Misión', icon: 'ri-focus-3-line' },
    { id: 'vision' as const, label: 'Visión', icon: 'ri-eye-line' },
  ];

  const contentMap: Record<string, string> = {
    historia: profile.history,
    mision: profile.mission,
    vision: profile.vision,
  };

  return (
    <div className="space-y-8">
      {/* ── HEADER CARD: Cover + Logo + Info ── */}
      <div className="bg-background-50 border border-background-200/70 rounded-2xl overflow-hidden">
        {/* Cover image */}
        <div className="relative h-40 md:h-48 bg-gradient-to-r from-primary-500/15 via-accent-500/15 to-secondary-500/15">
          <img
            src={station.coverUrl}
            alt={station.name}
            className="w-full h-full object-cover"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/60 via-foreground-950/20 to-transparent"></div>
        </div>

        {/* Logo + Title overlay */}
        <div className="relative px-4 md:px-6 -mt-14 pb-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-end gap-4">
            <div className="w-24 h-24 md:w-28 md:h-28 rounded-2xl overflow-hidden border-[4px] border-background-50 bg-foreground-200 shadow-lg shrink-0">
              <img src={station.logoUrl} alt={station.name} className="w-full h-full object-cover" />
            </div>
            <div className="flex-1 min-w-0 pb-1">
              <h1 className="text-xl md:text-2xl font-bold text-foreground-950">{station.name}</h1>
              <p className="text-sm text-foreground-500 mt-0.5">{profile.frequency}</p>
            </div>
          </div>
        </div>

        {/* Info Grid */}
        <div className="px-4 md:px-6 pb-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <InfoItem icon="ri-map-pin-line" label="Ciudad" value={profile.city} />
            <InfoItem icon="ri-global-line" label="País" value={profile.country} />
            <InfoItem icon="ri-calendar-line" label="Fundada" value={`${profile.foundedYear}`} />
            <InfoItem icon="ri-signal-tower-line" label="Frecuencia" value={profile.frequency} />
            <InfoItem icon="ri-folder-line" label="Categorías" value={profile.categories.join(', ')} />
            <InfoItem icon="ri-translate" label="Idiomas" value={profile.languages.join(', ')} />
          </div>
        </div>
      </div>

      {/* ── HISTORIA / MISIÓN / VISIÓN ── */}
      <div className="bg-background-50 border border-background-200/70 rounded-2xl overflow-hidden">
        {/* Tab pills */}
        <div className="flex items-center gap-1 p-3 bg-background-100/50 border-b border-background-200/50 overflow-x-auto">
          {tabs.map((tab) => {
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap shrink-0 ${
                  isActive
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-200/60 text-foreground-600 hover:bg-background-200'
                }`}
              >
                <i className={`${tab.icon} text-sm`}></i>
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Content */}
        <div className="p-4 md:p-6">
          <p className="text-foreground-700 text-[15px] leading-relaxed">{contentMap[activeTab]}</p>
        </div>
      </div>

      {/* ── HORARIOS ── */}
      <div className="bg-background-50 border border-background-200/70 rounded-2xl overflow-hidden">
        <div className="px-4 md:px-6 py-4 border-b border-background-200/50 flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg bg-accent-100 flex items-center justify-center">
            <i className="ri-time-line text-accent-600 text-base"></i>
          </div>
          <div>
            <h2 className="text-base font-bold text-foreground-950">Horarios de Programación</h2>
            <p className="text-xs text-foreground-500">Conoce todos los programas de {station.name}</p>
          </div>
        </div>
        <div className="p-4 md:p-6">
          <ScheduleTable schedule={profile.schedule} />
        </div>
      </div>
    </div>
  );
}